// import { format, isValid, parse } from "date-fns";
import { readCsvFileStream, readSheet, readWorkSheet } from './helpers'

/**
 * @typedef {Object} ProcessFileConfig
 * @property {number} rowNumber
 * @property {function(number):void} onProgress
 * @property {string} forceCsv // エクセルをCSVとして読み込みする
 */

/**
 * @function processFile
 * @param {File} file
 * @param {ProcessFileConfig} config
 * @returns {Promise<{ Headers: string[]; RecordCount: number; SheetId?: string; SheetName?: string }[]>}
 */
export const processFile = async (file, config) => {
  const { rowNumber = 1, onProgress, forceCsv = '' } = config ?? {}

  if (file.type === 'text/csv' || file.type === 'application/vnd.ms-excel' || forceCsv) {
    const gen = readCsvFileStream(file, 1000, onProgress)

    let firstRead = null
    let counter = 0
    const rows = []

    for await (const chunk of gen) {
      const modifiedChunk = !firstRead ? chunk.slice(1) : chunk

      if (!firstRead) {
        firstRead = chunk[0]
      }

      counter += chunk.length
      rows.push(...modifiedChunk)
    }

    return [
      {
        SheetName: forceCsv || 0,
        Headers: firstRead,
        RecordCount: counter - 1,
        Records: rows
      }
    ]
  } else {
    const sheets = await readWorkSheet(file, onProgress)

    const sheetList = await Promise.all(
      sheets.map(async (sheet) => {
        try {
          const { headers = [], records = [] } = readSheet(sheet.worksheet, rowNumber)

          return {
            SheetName: sheet.sheetName,
            SheetId: sheet.sheetId,
            Headers: headers,
            RecordCount: records.length,
            Records: records
          }
        } catch (error) {
          console.error('error', error)
          return {
            SheeetName: sheet.sheetName,
            SheetId: sheet.sheetId,
            Headers: [['正しいファイルをアップロードしてください']],
            RecordCount: 0,
            Records: [['正しいファイルをアップロードしてください']]
          }
        }
      })
    )

    return sheetList
  }
}

// ファイルアップロードのフック
export const useFileUpload = () => {
  /** @function processFiles 複数ファイルを読み込み */
  const processFiles = async (files) => files.map((e) => processFile(e))

  /**
   * @function processFiles 複数ファイルを読み込み
   * @param {HTMLInputElement} e
   */
  const handleFileUpload = (e) => {
    const fileElement = e

    if (!fileElement.files.length) return

    // const excelFormats = [
    //   ...['.xlsx', '.xlsm', '.xlsb', '.xltx', '.xltm'],
    //   ...['.xls', '.xlt', '.xlw', '.xlam', '.ods'],
    //   ...['.xml', '.csv', '.txt', '.wk1', '.wk3'],
    //   ...['.wk4', '.dbf', '.dif', '.slk']
    // ]

    // const excelFormats = [
    //   'text/csv',
    //   'application/vnd.ms-excel',
    //   'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    // ]

    const excelFormats = ['csv', 'xlsx']

    const files = []

    for (const file of fileElement.files) {
      const fileType = String(file.name).split('.').pop()

      if (!excelFormats.some((x) => fileType === x)) {
        throw new Error('正しい形式のファイルをアップロードしてください')
      }

      files.push({
        meta: {
          FileName: file.name,
          FileSize: file.size,
          FileType: fileType !== 'csv' ? 'excel' : 'csv'
        },
        data: file
      })
    }

    fileElement.value = ''

    return files
  }

  /**
   * @function openFileUpload アップロードウィンドウを開くための関数
   * @param {Event} e
   * @param {HTMLInputElement} fileElement
   * @returns
   */
  const openFileUpload = (e, fileElement) => {
    e.stopPropagation()
    if (!fileElement) return
    fileElement.click()
  }

  /**
   * @function handleFileDragOver ドラッグを止めるため
   * @param {Event} e
   * @returns
   */
  const handleFileDragOver = (e) => {
    e.preventDefault()
  }

  /**
   * @function handleFileDrop ドロップ処理
   * @param {Event} e
   * @param {HTMLInputElement} fileElement
   * @returns
   */
  const handleFileDrop = (e, fileElement) => {
    e.preventDefault()

    const items = e.dataTransfer.items
    if (Array.from(items)?.some((e) => e.kind !== 'file')) return

    const files = e.dataTransfer.files

    fileElement.files = files
    fileElement.dispatchEvent(new Event('change'))
  }

  /**
   * @function removeFile アップロードウィンドウを開くための関数
   * @param {Event} e
   * @param {HTMLInputElement} fileElement
   * @returns
   */
  const removeFile = (e, fileElement) => {
    e.stopPropagation()

    if (!fileElement) return

    fileElement.files = null
  }

  return {
    removeFile,
    handleFileUpload,
    handleFileDrop,
    handleFileDragOver,
    openFileUpload,
    processFile,
    processFiles
  }
}
